/**
 * FeatureFlagGuard - Component for conditional rendering based on feature flags
 *
 * Wraps components to control their visibility based on feature flag status.
 * Supports two modes: route protection (redirect) and conditional rendering (show/hide).
 *
 * @component
 * @example
 * // Route protection - redirects if feature disabled
 * <FeatureFlagGuard feature="reports">
 *   <ReportsComponent />
 * </FeatureFlagGuard>
 *
 * // Conditional rendering - hides if feature disabled
 * <FeatureFlagGuard feature="promotions" mode="conditional">
 *   <PromotionsButton />
 * </FeatureFlagGuard>
 *
 * // Custom fallback when disabled
 * <FeatureFlagGuard feature="beta" mode="conditional" fallback={<ComingSoonMessage />}>
 *   <BetaFeature />
 * </FeatureFlagGuard>
 */
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import FeatureFlagService from "../../services/FeatureFlagService";
import GuardFallbackRedirect from "../../routing/GuardFallbackRedirect";
import { activeProducts } from "../../routing/guardTriage";
import { isGamePassFeatureEnabled } from "../../utils/gamePassFeature";
import { AppStrings } from "../../utils/constants/AppStrings";
import { isDevModeEnv } from "../../hooks/useIsDevMode";

/**
 * FeatureFlagGuard component props
 *
 * At least one of `feature`, `product`, or `innerFeatureFlags` should be provided.
 * When none are provided the guard renders as disabled (unless `developmentMode` is on
 * and the environment is allowed).
 *
 * @typedef {Object} FeatureFlagGuardProps
 * @property {React.ReactNode} children - Components to render when feature is enabled
 * @property {string} [feature] - Feature name to check (e.g., 'reports', 'promotions')
 * @property {string} [product] - Trivelta Product scope_key (e.g. 'sportsbook', 'casino'). Resolved via FeatureFlagService.isProductActive (data from /platform/v2/config).
 * @property {boolean} [catalogOnly=false] - When used with `product`, resolve only against TCM V2 catalog presence via FeatureFlagService.isProductKeyActive.
 * @property {string[]} [innerFeatureFlags=[]] - Inner feature flags to check (e.g., ['transactions_wallet', 'transactions_overview', 'transactions_reports', 'manual_withdrawal_review'])
 * @property {'any'|'all'} [innerFlagsMode='any'] - How to evaluate inner feature flags
 * @property {'or'|'and'} [combineMode='or'] - How to combine feature + inner flags + product when multiple are provided
 * @property {string} [redirectTo] - Where to redirect when feature disabled (route mode). When omitted (or when it points at the current path), the guard walks `REDIRECT_PRIORITY` for the highest-priority page the admin can access — see `GuardFallbackRedirect`.
 * @property {React.ReactNode} [fallback=null] - What to show when feature disabled (conditional mode)
 * @property {'route'|'conditional'} [mode='route'] - Guard behavior: 'route' = redirect, 'conditional' = show/hide
 * @property {boolean} [developmentMode=false] - Environment guard; can work standalone when no feature flags are provided
 */
/** @param {FeatureFlagGuardProps} props */
const FeatureFlagGuard = ({
  children,
  feature,
  product,
  catalogOnly = false,
  innerFeatureFlags = [],
  innerFlagsMode = "any",
  combineMode = "or",
  redirectTo,
  fallback = null, // What to show when disabled (null = nothing)
  mode = "route", // 'route' = redirect, 'conditional' = show/hide
  developmentMode = false,
}) => {
  const [isEnabled, setIsEnabled] = useState(null); // null = loading
  const [loading, setLoading] = useState(true);
  const gamePassPlatformEnabled = useSelector((state) =>
    feature === "gamePass"
      ? isGamePassFeatureEnabled(state.config?.appConfig?.features_status_config)
      : false,
  );

  // Serialize innerFeatureFlags to detect actual content changes
  // This prevents re-renders when array reference changes but content is the same
  const innerFlagsKey = useMemo(
    () => innerFeatureFlags.join(","),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    innerFeatureFlags,
  );

  useEffect(() => {
    /**
     * Check if the specified feature is enabled
     *
     * Initializes the feature service if needed and checks the feature status.
     * Falls back to disabled state on any errors for security.
     */
    const checkFeature = async () => {
      try {
        setLoading(true);
        const hasFeature = Boolean(feature);
        const hasProduct = Boolean(product);
        const hasInnerFlags = innerFeatureFlags.length > 0;

        if (developmentMode) {
          // Same case-insensitive match as Sider / protectedElement
          // (`isDevModeEnv`). TCM may serve `Test03` / `test03` interchangeably.
          if (!isDevModeEnv()) {
            setIsEnabled(false);
            return;
          }

          // Allow usage as a pure environment gate without feature flags.
          if (!hasFeature && !hasProduct && !hasInnerFlags) {
            setIsEnabled(true);
            return;
          }
        }

        // Case-insensitive: PAM_APP_NAME comes from remotely served TCM
        // strings, so an exact-case match silently disables the bypass on
        // value drift.
        const isPikkemClientApp = ["rebet", "test03 pam"].includes(
          (AppStrings?.PAM_APP_NAME ?? "").toLowerCase(),
        );
        if (isPikkemClientApp && feature === "pikkem") {
          setIsEnabled(true);
          return;
        }

        // Ensure service is initialized and features+products are loaded
        await FeatureFlagService.initialize();

        // TODO(PAMENG-1820): during rollout `feature` doubles as the non-dev
        // fallback for `product` (`isProductActive` folds it in). Once v2 is
        // authoritative everywhere:
        //   1. Drop the second arg: `isProductActive(product)`.
        //   2. Drop the `&& isDevModeEnv()` guard in `protectedElement.tsx`.
        //   3. Delete the 4 legacy mappings in `featureFlagConfig.js`
        //      (sportsbook_v2, casino, poker, pikkem).
        //   4. Strip `feature="..."` props from Sider, UserManager,
        //      UserManagerHeader, and SportsbookFM (grep the file for
        //      `// fallback for ... (PAMENG-1820)`).
        const productEnabled = hasProduct
          ? catalogOnly
            ? FeatureFlagService.isProductKeyActive(product)
            : FeatureFlagService.isProductActive(product, feature)
          : false;

        // `feature` is consumed above when `product` is set — don't double-count.
        const featureEnabled = hasFeature && !hasProduct
          ? FeatureFlagService.isEnabled(feature)
          : false;
        const innerFlagsEnabled = hasInnerFlags
          ? innerFlagsMode === "all"
            ? innerFeatureFlags.every((flag) =>
                FeatureFlagService.isEnabled(flag),
              )
            : innerFeatureFlags.some((flag) =>
                FeatureFlagService.isEnabled(flag),
              )
          : false;

        // Combine gates: `and` = every, otherwise `some`. No gates = disabled.
        // When `product` is set, `feature` was already folded in above.
        const signals = [];
        if (hasFeature && !hasProduct) signals.push(featureEnabled);
        if (hasProduct) signals.push(productEnabled);
        if (hasInnerFlags) signals.push(innerFlagsEnabled);

        let enabled = false;
        if (signals.length > 0) {
          enabled =
            combineMode === "and"
              ? signals.every(Boolean)
              : signals.some(Boolean);
        }

        if (feature === "gamePass") {
          enabled = enabled || gamePassPlatformEnabled;
        }

        setIsEnabled(enabled);
      } catch (error) {
        // Fail closed — the router needs a synchronous decision, so we
        // hide/redirect on any error instead of surfacing it like
        // `useTriveltaProduct` does. If you need soft-fail, add a
        // `failOpen` prop rather than relaxing this default.
        console.error(error)
        setIsEnabled(false);
      } finally {
        setLoading(false);
      }
    };
    checkFeature();
    // `innerFeatureFlags` is intentionally omitted — `innerFlagsKey` is the
    // serialized content-based dep that protects against reference-only
    // changes (e.g. inline `[]` defaults) triggering needless re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    feature,
    product,
    catalogOnly,
    innerFlagsKey,
    innerFlagsMode,
    combineMode,
    developmentMode,
    gamePassPlatformEnabled,
  ]);

  // Show loading state while checking feature
  if (loading) {
    return mode === "conditional" ? null : (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  // Handle disabled features based on mode
  if (!isEnabled) {
    if (mode === "conditional") {
      // Conditional mode: hide component or show fallback
      // Used for sidebar buttons, optional UI elements
      return fallback;
    } else {
      // Triage log: a route-mode gate failure is usually a product-catalog
      // problem (e.g. `common` missing from /platform/v2/config), which the
      // active-products list makes immediately visible in the console.
      console.warn(
        `[FeatureFlagGuard] route gate failed at ${window.location.pathname}`,
        {
          product: product ?? null,
          feature: feature ?? null,
          innerFeatureFlags,
          activeProducts: activeProducts(),
        },
      );
      // Route mode: redirect to a page the admin can access. An explicit
      // `redirectTo` is honoured unless it points at the current path (the
      // old default of `/kpis` self-redirect-looped when the KPIs route's
      // own gate failed); otherwise the priority walk picks the target.
      return <GuardFallbackRedirect redirectTo={redirectTo} />;
    }
  }

  // Feature is enabled - render children normally
  return children;
};

export default FeatureFlagGuard;
